/**
 * MbuziplusCourse.jsx
 * Course home — MbuziPlus: Dairy Goat Farming Mastery
 *
 * UPDATED: Accounts are keyed by ACCESS TOKEN, not phone number.
 *      Full name is already known from the account created by staff
 *      (imfuyo_academy/{token}), so on first entry the learner is only
 *      asked for their Cohort number (required) and Email (optional) —
 *      no name field. That data is saved to:
 *        imfuyo_academy/{token}/mbuziplus_course/{token}
 *      Subsequent visits skip the gate and greet by name.
 *
 * NEW (this revision): The lesson list is a single top-to-bottom column
 *      (like the "One farmer, seven connected services" pillar list in
 *      MbuziPlusParent.jsx, but stacked, not gridded). Each topic is a row
 *      with a slim always-visible header (badge, title, subtitle, meta,
 *      chevron). Only one topic is expanded at a time — a single
 *      `openTopic` id in state means opening a new row's full hero panel
 *      (image, overlay badges, big title, teaser, Start Lesson button)
 *      automatically collapses whichever row was open before. On first
 *      load, the first live topic starts expanded with its image showing;
 *      moving to the next topic closes the first and opens the next, image
 *      and all, in the same spot in the list.
 *
 * Props:
 *   isDark   : boolean
 *   user     : { token: string, fullName: string, phone: string, programmes: string[] }
 *   onBack   : () => void
 *   onLogout : () => void
 */

import { useState, useEffect } from "react";
import {
  ChevronLeft, ChevronRight, LogOut, Lock, Play, ArrowRight,
  Clock, BookOpen, CheckCircle, Leaf, Mail, Hash,
  ChevronDown,
} from "lucide-react";

import { db } from "../../Libs/firebase-config.mjs";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

import BreedSelection from "./BreedSelection.jsx";
import DairyGoatAnatomy from "./DairyGoatAnatomy.jsx";
import CourseProgress from "./CourseProgress";
import CourseMap from "./CourseMap.jsx";
import Planner from "./Planner.jsx";
import { MODULES, PARTS, TOTAL_HOURS } from "./courseMap";
import ModuleReader from "./ModuleReader";
import { GENERIC, itemsOf } from "./modules";
import MbuziPlusExam from "./MbuziPlusExam.jsx";
import { Award } from "lucide-react";

// Lessons, video, quiz and journal steps in each open topic (used for the progress percentage)
const OPEN_TOPIC_ITEMS = { "breed-selection": 14, anatomy: 12, ...Object.fromEntries(Object.values(GENERIC).map((m) => [m.id, itemsOf(m)])) };

/* ─── animations / layout ────────────────────────────────── */
const globalCss = `
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes scaleIn {
    from { opacity:0; transform:scale(0.97); }
    to   { opacity:1; transform:scale(1); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes gateIn {
    from { opacity:0; transform:translateY(24px) scale(0.97); }
    to   { opacity:1; transform:translateY(0) scale(1); }
  }
  @keyframes welcomePop {
    0%   { opacity:0; transform:scale(0.82) translateY(6px); }
    70%  { transform:scale(1.03) translateY(-2px); }
    100% { opacity:1; transform:scale(1) translateY(0); }
  }
  @keyframes heroPanelIn {
    from { opacity:0; transform: translateY(6px); }
    to   { opacity:1; transform: translateY(0); }
  }
  .topic-hero-img { transition: transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94); }
  .topic-hero-img:hover { transform: scale(1.03); }

  .mc-topics-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .mc-accent-hover::before {
    content: "";
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: #3E7A32;
    transform: scaleX(0); transform-origin: left;
    transition: transform 0.35s ease;
    pointer-events: none;
    z-index: 2;
  }
  .mc-accent-hover:hover::before { transform: scaleX(1); }
`;

/* ─── curriculum ─────────────────────────────────────────── */
const TOPICS = [
  ...Object.values(GENERIC).map((m) => ({ id: m.id, seq: 0, title: m.title, subtitle: m.tagline, duration: "About 4 hours", lessons: m.lessons.length, level: "Beginner", status: "live", teaser: m.summary, desktopImage: null, mobileImage: null })),
  {
    id: "breed-selection",
    seq: 1,
    title: "Breed Selection",
    subtitle: "Find the goat that fits your land, climate, and budget",
    desktopImage: "/academy/goat-pen.jpg",
    mobileImage:  "/academy/goat-pen.jpg",
    duration: "About 4 hours",
    lessons: 11,
    level: "Beginner",
    status: "live",
    teaser: "Compare Alpine, Toggenburg, Saanen and Galla across milk yield, climate resilience, and true cost of ownership — then choose with confidence.",
  },
  {
    id: "goat-anatomy",
    seq: 2,
    title: "Goat Anatomy",
    subtitle: "Read your goat's body before you read its records",
    desktopImage: "/academy/goat-portrait.jpg",
    mobileImage:  "/academy/goat-portrait.jpg",
    duration: "About 4 hours",
    lessons: 9,
    level: "Beginner",
    status: "live",
    teaser: "The skeleton, the four-chamber stomach, and the udder — a practical tour of goat anatomy so you can judge conformation and spot trouble early.",
  },
];

const COHORTS = [1, 2, 3, 4, 5, 6, 7, 8];

/* ─── helpers ────────────────────────────────────────────── */
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
}

function Spinner({ size = 16, color = "#fff" }) {
  return (
    <span style={{
      width: size, height: size,
      border: `2px solid rgba(255,255,255,0.25)`,
      borderTopColor: color,
      borderRadius: "50%",
      display: "inline-block",
      animation: "spin 0.75s linear infinite",
      flexShrink: 0,
    }} />
  );
}

function isValidEmail(value) {
  if (!value) return true; // optional — empty is fine
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/* ─────────────────────────────────────────────────────────── *
 *  ONBOARDING GATE
 *  Full name is already known (from the account staff created).
 *  We only ask for Cohort (required) and Email (optional).
 * ─────────────────────────────────────────────────────────── */
function OnboardingGate({ isDark, user, isMobile, onComplete }) {
  const [cohort,   setCohort]   = useState("");
  const [email,    setEmail]    = useState("");
  const [errors,   setErrors]   = useState({});
  const [saving,   setSaving]   = useState(false);
  const [apiErr,   setApiErr]   = useState("");

  const bg       = isDark ? "#111827" : "#fff";
  const surface  = isDark ? "#0f1f13" : "#f4fef6";
  const border   = isDark ? "#1e3321" : "#D5E8CB";
  const textMain = isDark ? "#f0f7f0" : "#0d1a0f";
  const textSub  = isDark ? "#6b8a6e" : "#5a7a5d";
  const labelCol = isDark ? "#d1fae5" : "#14301A";
  const inputBg  = isDark ? "#111827" : "#fafff8";

  const firstName = (user.fullName || "").split(" ")[0] || "there";

  const validate = () => {
    const errs = {};
    if (!cohort)
      errs.cohort = "Select your cohort";
    if (email && !isValidEmail(email))
      errs.email = "Enter a valid email address, or leave it blank";
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    setApiErr("");

    try {
      const ref = doc(db, "imfuyo_academy", user.token, "mbuziplus_course", user.pid || user.token);
      await setDoc(ref, {
        fullName:  user.fullName || "",
        phone:     user.phone || "",
        cohort:    Number(cohort),
        email:     email.trim() || null,
        joinedAt:  serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      onComplete({
        fullName: user.fullName || "",
        cohort: Number(cohort),
        email: email.trim() || null,
      });
    } catch (err) {
      console.error(err);
      setApiErr("Couldn't save your details. Check your connection and try again.");
      setSaving(false);
    }
  };

  const inputStyle = (hasErr) => ({
    width: "100%",
    padding: "0 14px 0 44px",
    height: "52px",
    borderRadius: "12px",
    border: `1.5px solid ${hasErr ? "#ef4444" : isDark ? "#1e3321" : "#D5E8CB"}`,
    fontSize: "15px",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    background: inputBg,
    color: textMain,
    transition: "border-color 0.15s",
  });

  return (
    <div style={{
      minHeight: "100vh",
      background: isDark ? "#0d1a0f" : "#f5f9f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px",
      fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    }}>
      <style>{globalCss}</style>

      <div style={{
        width: "min(480px, 100%)",
        animation: "gateIn 0.45s cubic-bezier(0.16,1,0.3,1) both",
      }}>

        {/* Card */}
        <div style={{
          background: bg,
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: isDark
            ? "0 24px 72px rgba(0,0,0,0.55)"
            : "0 24px 72px rgba(0,0,0,0.1)",
          border: `1px solid ${border}`,
        }}>

          {/* Green accent top strip */}
          <div style={{
            height: "5px",
            background: "linear-gradient(90deg, #285020, #3E7A32, #8DBB7E)",
          }} />

          {/* Header area */}
          <div style={{
            padding: isMobile ? "28px 24px 20px" : "36px 36px 24px",
            background: surface,
            borderBottom: `1px solid ${border}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <Leaf size={13} color="#3E7A32" />
              <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: isDark ? "#3a7040" : "#5a9060" }}>
                MbuziPlus · Dairy Goat Farming Mastery
              </span>
            </div>

            <h1 style={{
              margin: "0 0 8px",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: isMobile ? "26px" : "30px",
              fontWeight: 700,
              color: textMain,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}>
              Welcome, {firstName} —<br />
              <span style={{ color: "#3E7A32" }}>just one more step</span>
            </h1>
            <p style={{ margin: 0, fontSize: "14px", color: textSub, lineHeight: 1.65 }}>
              Kindly select your cohort and enter your email (optional) so we can personalise your learning experience.
            </p>
          </div>

          {/* Form */}
          <div style={{ padding: isMobile ? "24px" : "32px 36px 36px" }}>

            {apiErr && (
              <div style={{
                background: isDark ? "#450a0a" : "#fef2f2",
                border: "1.5px solid #fecaca",
                borderRadius: "10px",
                padding: "12px 14px",
                marginBottom: "20px",
                fontSize: "13px",
                color: isDark ? "#fca5a5" : "#dc2626",
                lineHeight: 1.5,
              }}>
                {apiErr}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

              {/* Cohort selector */}
              <div>
                <label style={{
                  display: "block",
                  fontSize: "12px", fontWeight: 700,
                  color: labelCol,
                  marginBottom: "7px",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}>
                  Cohort Number <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <Hash
                    size={15}
                    color={errors.cohort ? "#ef4444" : "#9ca3af"}
                    style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", zIndex: 1 }}
                  />
                  <select
                    value={cohort}
                    onChange={e => { setCohort(e.target.value); setErrors({}); setApiErr(""); }}
                    style={{
                      ...inputStyle(errors.cohort),
                      paddingRight: "40px",
                      appearance: "none",
                      cursor: "pointer",
                    }}
                    onFocus={e => e.target.style.borderColor = "#3E7A32"}
                    onBlur={e => e.target.style.borderColor = errors.cohort ? "#ef4444" : (isDark ? "#1e3321" : "#D5E8CB")}
                  >
                    <option value="">Select your cohort…</option>
                    {COHORTS.map(n => (
                      <option key={n} value={n}>Cohort {n}</option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    color="#9ca3af"
                    style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                  />
                </div>
                {errors.cohort && (
                  <p style={{ margin: "5px 0 0", fontSize: "12px", color: "#ef4444" }}>{errors.cohort}</p>
                )}
                <p style={{ margin: "5px 0 0", fontSize: "12px", color: textSub }}>
                  Your cohort number was shared when you registered for MbuziPlus.
                </p>
              </div>

              {/* Email (optional) */}
              <div>
                <label style={{
                  display: "block",
                  fontSize: "12px", fontWeight: 700,
                  color: labelCol,
                  marginBottom: "7px",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}>
                  Email <span style={{ color: textSub, textTransform: "none", fontWeight: 500 }}>(optional)</span>
                </label>
                <div style={{ position: "relative" }}>
                  <Mail
                    size={15}
                    color={errors.email ? "#ef4444" : "#9ca3af"}
                    style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setErrors({}); setApiErr(""); }}
                    placeholder="e.g. jane.wanjiru@email.com"
                    style={inputStyle(errors.email)}
                    onFocus={e => e.target.style.borderColor = "#3E7A32"}
                    onBlur={e => e.target.style.borderColor = errors.email ? "#ef4444" : (isDark ? "#1e3321" : "#D5E8CB")}
                  />
                </div>
                {errors.email && (
                  <p style={{ margin: "5px 0 0", fontSize: "12px", color: "#ef4444" }}>{errors.email}</p>
                )}
              </div>

              {/* CTA */}
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  marginTop: "6px",
                  width: "100%",
                  padding: "15px",
                  borderRadius: "13px",
                  border: "none",
                  background: saving
                    ? (isDark ? "#1a3a22" : "#E3EEDD")
                    : "linear-gradient(135deg, #285020, #3E7A32)",
                  color: saving ? (isDark ? "#8DBB7E" : "#14301A") : "#fff",
                  fontSize: "15px",
                  fontWeight: 800,
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "9px",
                  letterSpacing: "-0.01em",
                  boxShadow: saving ? "none" : "0 4px 24px rgba(34,197,94,0.35)",
                  transition: "opacity 0.15s, transform 0.12s",
                }}
                onMouseEnter={e => { if (!saving) { e.currentTarget.style.opacity = "0.92"; e.currentTarget.style.transform = "scale(1.01)"; }}}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1)"; }}
              >
                {saving
                  ? <><Spinner size={15} color="#3E7A32" /> Saving your details…</>
                  : <>Enter the course <ArrowRight size={16} /></>
                }
              </button>

            </div>
          </div>
        </div>

        {/* Access token reminder below card */}
        <p style={{
          textAlign: "center",
          fontSize: "12px",
          color: isDark ? "#3a5c3d" : "#9abd9a",
          marginTop: "16px",
          fontFamily: "inherit",
        }}>
          Signed in with access code {user.token}{user.pid ? ` (group member)` : ""}
        </p>
      </div>
    </div>
  );
}

/* ─── TOPIC ROW — slim always-visible header ─────────────────────────
   Badge, title, subtitle, quick meta, chevron. Click toggles the hero
   panel below it. Stays visible whether the row is open or closed so
   the whole list keeps its top-to-bottom rhythm. ────────────────────── */
function TopicRowHeader({ topic, isDark, open, onToggle, totalCount }) {
  const locked = topic.status !== "live";
  const textMain = isDark ? "#f0f7f0" : "#0d1a0f";
  const textSub  = isDark ? "#6b8a6e" : "#5a7a5d";
  const badgeBg  = locked ? (isDark ? "#1a2a1e" : "#eef4ee") : (isDark ? "#14301A" : "#E3EEDD");
  const badgeCol = locked ? (isDark ? "#4b5563" : "#9ca3af") : (isDark ? "#8DBB7E" : "#285020");

  return (
    <div
      className="mc-accent-hover"
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      style={{
        position: "relative",
        display: "flex", alignItems: "center", gap: "14px",
        padding: "16px 20px",
        cursor: "pointer",
      }}
    >
      <div style={{
        flexShrink: 0, width: "38px", height: "38px", borderRadius: "10px",
        background: badgeBg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {locked
          ? <Lock size={14} color={badgeCol} />
          : <span style={{ fontSize: "12.5px", fontWeight: 800, color: badgeCol }}>
              {String(topic.seq === 1 ? 4 : 5).padStart(2, "0")}
            </span>
        }
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: "14.5px", fontWeight: 700, color: textMain,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {topic.title}
          <span style={{ fontSize: "11px", fontWeight: 600, color: textSub, marginLeft: "8px" }}>
            Module {topic.seq === 1 ? 4 : 5} of 42
          </span>
        </p>
        <p style={{
          margin: "2px 0 0", fontSize: "12px", color: textSub, fontWeight: 500,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {topic.subtitle}
        </p>
      </div>

      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontSize: "11px", color: textSub, display: "flex", alignItems: "center", gap: "4px" }}>
          <Clock size={11} /> {topic.duration}
        </span>
        <span style={{
          fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
          color: badgeCol, background: badgeBg, padding: "3px 10px", borderRadius: "50px",
        }}>
          {locked ? "Locked" : "Open"}
        </span>
        <ChevronRight
          size={16}
          color={textSub}
          style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}
        />
      </div>
    </div>
  );
}

/* ─── TOPIC PANEL — the full hero, shown only while a row is open ─────
   Live topics: image + gradient overlay + badges + big title + teaser
   + Start Lesson button — the exact content the old HeroTopicCard
   carousel used, just embedded inline in the stack instead of a
   separate rotating banner. Locked topics: a muted placeholder in
   place of the (nonexistent) image, plus the teaser and a notify note. */
function TopicPanel({ topic, isDark, isMobile, onStart }) {
  const locked = topic.status !== "live";
  const img = isMobile ? topic.mobileImage : topic.desktopImage;
  const [imgOk, setImgOk] = useState(true);

  if (locked) {
    const textSub = isDark ? "#6b8a6e" : "#5a7a5d";
    return (
      <div style={{
        padding: isMobile ? "0 18px 22px" : "0 22px 26px",
        animation: "heroPanelIn 0.35s cubic-bezier(0.16,1,0.3,1) both",
      }}>
        <div style={{
          borderRadius: "16px",
          border: `1.5px dashed ${isDark ? "#243529" : "#dfeadf"}`,
          background: isDark ? "#0f1f13" : "#f6faf6",
          padding: isMobile ? "28px 20px" : "34px 28px",
          display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "10px",
        }}>
          <div style={{
            width: "44px", height: "44px", borderRadius: "50%",
            background: isDark ? "#1a2a1e" : "#eef4ee",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Lock size={18} color={isDark ? "#4b5563" : "#9ca3af"} />
          </div>
          <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: isDark ? "#9ca3af" : "#6b7280" }}>
            Coming soon
          </p>
          <p style={{ margin: 0, fontSize: "13px", color: textSub, lineHeight: 1.65, maxWidth: "440px" }}>
            {topic.teaser}
          </p>
          <div style={{
            display: "flex", alignItems: "center", gap: "8px", marginTop: "8px",
            fontSize: "12px", color: textSub, fontWeight: 500,
            background: isDark ? "#111827" : "#fff",
            border: `1px solid ${isDark ? "#1e3321" : "#dff0e0"}`,
            borderRadius: "10px", padding: "9px 14px",
          }}>
            <Leaf size={13} color="#3E7A32" style={{ flexShrink: 0 }} />
            You'll be notified on WhatsApp the moment this lesson unlocks.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? "0 18px 20px" : "0 22px 24px",
      animation: "heroPanelIn 0.35s cubic-bezier(0.16,1,0.3,1) both",
    }}>
      <div style={{ position: "relative", borderRadius: "18px", overflow: "hidden" }}>
        <div style={{ height: isMobile ? "300px" : "400px", overflow: "hidden", background: "#0d1a0f" }}>
          {imgOk && img ? (
            <img
              className="topic-hero-img"
              src={img}
              alt={topic.title}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              onError={() => setImgOk(false)}
            />
          ) : (
            <div style={{
              width: "100%", height: "100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isDark ? "#0f1f13" : "#132a1a",
            }}>
              <Leaf size={36} color="#3E7A32" style={{ opacity: 0.5 }} />
            </div>
          )}
        </div>

        <div style={{
          position: "absolute", inset: 0,
          background: isMobile
            ? "linear-gradient(to top, rgba(7,14,8,0.96) 0%, rgba(7,14,8,0.6) 45%, rgba(7,14,8,0.15) 70%, transparent 100%)"
            : "linear-gradient(to top, rgba(7,14,8,0.95) 0%, rgba(7,14,8,0.55) 40%, rgba(7,14,8,0.1) 65%, transparent 100%)",
        }} />

        <div style={{
          position: "absolute", top: "16px", left: "16px", right: "16px",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <span style={{
            background: "#3E7A32", color: "#fff",
            fontSize: "11px", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase",
            padding: "5px 14px", borderRadius: "50px",
            display: "flex", alignItems: "center", gap: "5px",
          }}>
            <Play size={9} fill="#fff" /> Open now
          </span>
          <span style={{
            background: "rgba(0,0,0,0.45)", backdropFilter: "blur(10px)",
            color: "#e5e7eb", fontSize: "11px", fontWeight: 600,
            padding: "5px 12px", borderRadius: "50px",
            border: "1px solid rgba(255,255,255,0.12)",
          }}>
            {topic.level}
          </span>
        </div>

        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          padding: isMobile ? "24px 20px 22px" : "30px 30px 28px",
        }}>
          <div style={{ display: "flex", gap: "16px", marginBottom: "10px", flexWrap: "wrap" }}>
            {[
              [Clock, topic.duration],
              [BookOpen, `${topic.lessons} lessons`],
            ].map(([Icon, label]) => (
              <span key={label} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#B7D9A8", fontWeight: 500 }}>
                <Icon size={12} color="#B7D9A8" /> {label}
              </span>
            ))}
          </div>

          <h2 style={{
            margin: "0 0 8px",
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: isMobile ? "24px" : "32px",
            fontWeight: 700, color: "#fff",
            lineHeight: 1.12, letterSpacing: "-0.02em",
          }}>
            {topic.title}
          </h2>

          <p style={{
            margin: "0 0 18px",
            fontSize: isMobile ? "13px" : "14.5px",
            color: "rgba(255,255,255,0.72)",
            lineHeight: 1.55, maxWidth: "520px",
          }}>
            {topic.teaser}
          </p>

          <button
            onClick={(e) => { e.stopPropagation(); onStart(); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: "9px",
              background: "#3E7A32", color: "#fff",
              border: "none", borderRadius: "14px",
              padding: isMobile ? "12px 20px" : "13px 24px",
              fontSize: "14px", fontWeight: 800,
              cursor: "pointer", fontFamily: "inherit",
              letterSpacing: "-0.01em",
              boxShadow: "0 4px 24px rgba(34,197,94,0.45)",
              transition: "background 0.15s, transform 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#285020"; e.currentTarget.style.transform = "scale(1.02)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#3E7A32"; e.currentTarget.style.transform = "scale(1)"; }}
          >
            <Play size={14} fill="#fff" /> Start Lesson <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── TOPIC CARD — row header + hero panel, one accordion item ───────── */
function TopicCard({ topic, isDark, isMobile, open, onToggle, onStart, totalCount }) {
  const cardBg    = isDark ? "#111827" : "#fff";
  const borderCol = open ? "#22c55e70" : (isDark ? "#1e3321" : "#d8edd8");

  return (
    <div style={{
      borderRadius: "18px",
      overflow: "hidden",
      background: cardBg,
      border: `1.5px solid ${borderCol}`,
      boxShadow: open
        ? (isDark ? "0 16px 40px rgba(0,0,0,0.45)" : "0 16px 40px rgba(0,0,0,0.1)")
        : "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }}>
      <TopicRowHeader topic={topic} isDark={isDark} open={open} onToggle={onToggle} totalCount={totalCount} />
      {open && (
        <TopicPanel topic={topic} isDark={isDark} isMobile={isMobile} onStart={onStart} />
      )}
    </div>
  );
}

/* ─── MAIN ───────────────────────────────────────────────── */
export default function MbuziplusCourse({ isDark = false, user, onBack, onLogout }) {
  const [activeView,  setActiveView]  = useState("home");
  const [profile,     setProfile]     = useState(null);   // { fullName, cohort, email }
  const [loadingProf, setLoadingProf] = useState(true);   // checking Firestore on mount
  const [isReturning, setIsReturning] = useState(false);  // did they already have a profile?
  const [openTopic,   setOpenTopic]   = useState(() => TOPICS.find(t => t.status === "live")?.id || null);
  const [doneItems,   setDoneItems]   = useState(0);
  const [perTopic,    setPerTopic]    = useState({});
  const [tab,         setTab]         = useState("course");
  const [exam,        setExam]        = useState(null);   // { score, total, percent, passed }
  const isMobile = useIsMobile();
  const totalItems = Object.values(OPEN_TOPIC_ITEMS).reduce((a, b) => a + b, 0);
  const pct = Math.min(100, Math.round((doneItems / totalItems) * 100));

  /* ── read the student's progress (runs on open and whenever they return to the course home) ── */
  useEffect(() => {
    if (!user?.token || activeView !== "home") return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "imfuyo_academy", user.token, "mbuziplus_course", user.pid || user.token));
        if (!snap.exists()) return;
        const d = snap.data();
        const count = (k) => Math.min(OPEN_TOPIC_ITEMS[k], Object.values((d[k] && d[k].completed) || {}).filter(Boolean).length);
        setDoneItems(Object.keys(OPEN_TOPIC_ITEMS).reduce((n, k) => n + count(k), 0));
        setPerTopic(Object.keys(OPEN_TOPIC_ITEMS).reduce((o, k) => ({ ...o, [k]: count(k) }), {}));
        setExam(d.exam || null);
      } catch (err) { console.error("progress:", err); }
    })();
  }, [user?.token, activeView]);

  /* ── fetch existing profile on mount — keyed by access token ── */
  useEffect(() => {
    async function fetchProfile() {
      try {
        const ref = doc(db, "imfuyo_academy", user.token, "mbuziplus_course", user.pid || user.token);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setProfile({
            fullName: data.fullName || user.fullName || "",
            cohort: data.cohort,
            email: data.email || "",
          });
          setIsReturning(true);
        }
      } catch (err) {
        console.error("Could not fetch profile:", err);
      } finally {
        setLoadingProf(false);
      }
    }
    if (user?.token) fetchProfile();
    else setLoadingProf(false);
  }, [user?.token]);

  const handleGateComplete = (data) => {
    setProfile(data);
    setIsReturning(false); // fresh enrolment
  };

  const handleStart = (id) => {
    if (id === "breed-selection") setActiveView("breed-selection");
    if (id === "goat-anatomy")    setActiveView("goat-anatomy");
    if (GENERIC[id])              setActiveView(id);
  };

  const toggleTopic = (id) => {
    setOpenTopic(prev => (prev === id ? null : id));
  };

  /* ── route: lessons ── */
  if (activeView === "breed-selection") {
    return (
      <>
        <CourseProgress user={user} isDark={isDark} />
        <BreedSelection
          isDark={isDark}
          user={user}
          onBack={() => setActiveView("home")}
        />
      </>
    );
  }

  if (activeView === "goat-anatomy") {
    return (
      <>
        <CourseProgress user={user} isDark={isDark} />
        <DairyGoatAnatomy
          isDark={isDark}
          user={user}
          onBack={() => setActiveView("home")}
        />
      </>
    );
  }

  if (GENERIC[activeView]) {
    return (
      <>
        <CourseProgress user={user} isDark={isDark} />
        <ModuleReader content={GENERIC[activeView]} isDark={isDark} user={user} onBack={() => setActiveView("home")} />
      </>
    );
  }

  if (activeView === "exam") {
    return (
      <MbuziPlusExam
        isDark={isDark}
        user={user}
        profile={profile}
        topics={TOPICS.filter(t => t.status === "live").map(t => t.title)}
        onBack={() => setActiveView("home")}
        onSaved={setExam}
      />
    );
  }

  /* ── loading profile check ── */
  if (loadingProf) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: isDark ? "#0d1a0f" : "#f5f9f5",
        fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
      }}>
        <style>{globalCss}</style>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
          <Spinner size={28} color="#3E7A32" />
          <p style={{ fontSize: "14px", color: isDark ? "#8DBB7E" : "#3E7A32", fontWeight: 600, margin: 0 }}>
            Loading your course…
          </p>
        </div>
      </div>
    );
  }

  /* ── gate: show if no profile yet (cohort + optional email only) ── */
  if (!profile) {
    return (
      <OnboardingGate
        isDark={isDark}
        user={user}
        isMobile={isMobile}
        onComplete={handleGateComplete}
      />
    );
  }

  /* ── course home ── */
  const firstName = (profile.fullName || user.fullName || "Friend").split(" ")[0];
  const bg        = isDark ? "#0d1a0f" : "#f5f9f5";
  const textMain  = isDark ? "#f0f7f0" : "#0d1a0f";
  const textSub   = isDark ? "#6b8a6e" : "#5a7a5d";
  const divider   = isDark ? "#1e3321" : "#d8edd8";

  const liveCount = TOPICS.filter(t => t.status === "live").length;

  return (
    <div style={{
      minHeight: "100vh",
      background: bg,
      fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
      paddingTop: "80px",
    }}>
      <style>{globalCss}</style>

      {/* ── inner nav strip ── */}
      <div style={{
        maxWidth: "980px",
        margin: "0 auto",
        padding: isMobile ? "20px 18px 0" : "28px 32px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
      }}>
        <button
          onClick={onBack}
          style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            background: "none",
            border: `1px solid ${isDark ? "#243529" : "#c8e0c8"}`,
            borderRadius: "50px",
            padding: "7px 16px",
            fontSize: "12px", fontWeight: 600,
            color: textSub, cursor: "pointer", fontFamily: "inherit",
            transition: "border-color 0.15s, color 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#3E7A32"; e.currentTarget.style.color = "#3E7A32"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? "#243529" : "#c8e0c8"; e.currentTarget.style.color = textSub; }}
        >
          <ChevronLeft size={13} /> All programmes
        </button>

        {user && (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {!isMobile && (
              <span style={{ fontSize: "12px", color: textSub }}>{user.token}</span>
            )}
            <button
              onClick={onLogout}
              style={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                background: "none",
                border: `1px solid ${isDark ? "#243529" : "#c8e0c8"}`,
                borderRadius: "50px",
                padding: "7px 14px",
                fontSize: "12px", fontWeight: 600,
                color: textSub, cursor: "pointer", fontFamily: "inherit",
                transition: "opacity 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.65"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        )}
      </div>

      {/* ── header: who you are, how far you have come, and what to do next ── */}
      {(() => {
        const nextTopic = TOPICS.filter(t => t.status === "live").find(t => (perTopic[t.id] || 0) < (OPEN_TOPIC_ITEMS[t.id] || 1)) || null;
        const nextMod = nextTopic && MODULES.find(m => m.id === nextTopic.id);
        const card = isDark ? "#13231a" : "#ffffff";
        const tabs = [["course", "My course"], ["plan", "Study plan"], ["exam", "Exam"]];
        return (
          <>
            <header style={{ maxWidth: "980px", margin: "0 auto", padding: isMobile ? "24px 18px 8px" : "32px 32px 8px" }}>
              <p style={{ margin: "0 0 6px", fontSize: "13px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#D9601A" }}>MbuziPlus Course · Cohort {profile.cohort}</p>
              <h1 style={{ margin: "0 0 18px", fontSize: isMobile ? "32px" : "44px", fontWeight: 800, color: textMain, lineHeight: 1.08, letterSpacing: "-0.03em" }}>
                {isReturning ? `Welcome back, ${firstName}.` : `Welcome, ${firstName}.`}
              </h1>

              <div style={{ background: card, border: `1px solid ${divider}`, borderRadius: "22px", padding: isMobile ? "18px" : "24px 28px", display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "center", justifyContent: "space-between", boxShadow: "0 10px 30px rgba(20,48,26,.07)" }}>
                <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#3E7A32" }}>{nextTopic ? (doneItems === 0 ? "Start here" : "Continue where you stopped") : "All open modules done"}</div>
                  <div style={{ fontSize: isMobile ? "22px" : "26px", fontWeight: 800, color: textMain, margin: "6px 0 10px", lineHeight: 1.2 }}>
                    {nextMod ? `Module ${nextMod.no}: ${nextMod.title}` : "Take your final exam"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} style={{ flex: "1 1 160px", maxWidth: "300px", height: "10px", borderRadius: "6px", background: isDark ? "#1e3321" : "#dbeedd", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "#3E7A32", transition: "width 0.6s ease" }} />
                    </div>
                    <span style={{ fontSize: "15px", color: textMain, fontWeight: 700 }}>{pct}% of the open lessons done</span>
                  </div>
                </div>
                <button
                  onClick={() => (nextTopic ? handleStart(nextTopic.id) : setTab("exam"))}
                  style={{ background: "#D9601A", color: "#fff", border: "none", borderRadius: "999px", padding: "16px 30px", fontSize: "17px", fontWeight: 800, cursor: "pointer", fontFamily: "inherit", width: isMobile ? "100%" : "auto" }}
                >
                  {nextTopic ? (doneItems === 0 ? "Start learning" : "Continue") : "Go to the exam"}
                </button>
              </div>
            </header>

            {/* ── tabs: one thing at a time ── */}
            <nav aria-label="Course sections" style={{ position: "sticky", top: 0, zIndex: 20, background: bg, borderBottom: `1px solid ${divider}`, marginTop: "18px" }}>
              <div style={{ maxWidth: "980px", margin: "0 auto", padding: isMobile ? "0 10px" : "0 32px", display: "flex", gap: isMobile ? "2px" : "8px", overflowX: "auto" }}>
                {tabs.map(([k, label]) => (
                  <button key={k} onClick={() => setTab(k)} aria-current={tab === k ? "page" : undefined}
                    style={{ background: "none", border: "none", borderBottom: `4px solid ${tab === k ? "#D9601A" : "transparent"}`, padding: isMobile ? "14px 12px" : "16px 18px", fontSize: "16px", fontWeight: tab === k ? 800 : 600, color: tab === k ? textMain : textSub, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                    {label}
                  </button>
                ))}
              </div>
            </nav>
          </>
        );
      })()}

      <main style={{ maxWidth: "980px", margin: "0 auto", padding: isMobile ? "24px 18px 80px" : "32px 32px 80px" }}>
        {tab === "course" && <CourseMap isDark={isDark} onStart={(id) => handleStart(id)} />}

        {tab === "plan" && <Planner isDark={isDark} user={user} />}

        {tab === "exam" && (
          <div style={{ padding: isMobile ? "20px" : "28px", borderRadius: "20px", background: isDark ? "#14301A" : "#E3EEDD", border: `1px solid ${divider}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "18px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", minWidth: 0, flex: "1 1 320px" }}>
              <Award size={30} color="#3E7A32" style={{ flexShrink: 0, marginTop: "2px" }} />
              <div>
                <p style={{ margin: 0, fontWeight: 800, color: textMain, fontSize: "20px" }}>Final exam and certificate</p>
                <p style={{ margin: "6px 0 0", fontSize: "16px", color: textSub, lineHeight: 1.6 }}>
                  {exam && exam.passed
                    ? `Passed with ${exam.percent}%. Open the exam to download your certificate again.`
                    : pct === 100
                      ? "100 multiple-choice questions drawn from all eight parts. Pass with 70% and download your certificate with your name."
                      : `Finish all open lessons to unlock the exam. You are ${pct}% of the way there.`}
                </p>
              </div>
            </div>
            <button
              onClick={() => setActiveView("exam")}
              disabled={pct < 100 && !(exam && exam.passed)}
              style={{ background: "#285020", color: "#fff", border: "none", borderRadius: "999px", padding: "14px 28px", fontSize: "16px", fontWeight: 800, fontFamily: "inherit", cursor: pct < 100 && !(exam && exam.passed) ? "not-allowed" : "pointer", opacity: pct < 100 && !(exam && exam.passed) ? 0.45 : 1 }}
            >
              {exam && exam.passed ? "Open exam and certificate" : exam ? "Retake the exam" : "Start the exam"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}